import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { usePartNumberSort } from './usePartNumberSort';
import SubTabToolbar from './SubTabToolbar';
import { useSubTabControls } from './useSubTabControls';

interface PolishFactoryItem {
  posting_date: string | null;
  sales_order: string;
  creation_date: string | null;
  date_of_update: string | null;
  drop_number: string;
  warehouse_code: string;
  bill_to: string;
  delivery_address: string;
  delivery_postcode: string;
  row_delivery_date: string | null;
  factory: string;
  customer_code: string;
  customer_name: string;
  part_number: string;
  description: string;
  open_qty: number;
  price_after_discount: number | null;
  row_total: number | null;
  document_total: number | null;
  warehouse_code2: string;
  pick_list_id: string;
  [key: string]: unknown;
}

interface PolishFactorySubTabProps {
  partNumber?: string;
}

export default function PolishFactorySubTab({ partNumber }: PolishFactorySubTabProps) {
  const [items, setItems] = useState<PolishFactoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { pageSize, setPageSize, searchInput, setSearchInput, search, page, setPage } = useSubTabControls(partNumber);
  const { ordering, icon: sortIcon, toggle: togglePartNumberSort } = usePartNumberSort(() => setPage(1));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = { page, page_size: pageSize };
      if (search) params['search'] = search;
      if (ordering) params['ordering'] = ordering;
      const res = await callOffAPI.getPolishFactory(params);
      const data = res.data as { results?: PolishFactoryItem[]; items?: PolishFactoryItem[]; count?: number; total_count?: number };
      setItems(data.results || data.items || []);
      setTotalCount(data.count || data.total_count || 0);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load Polish factory data';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, ordering]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div>
      <SubTabToolbar
        search={searchInput}
        onSearchChange={setSearchInput}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        placeholder="Search by part number or sales order…"
      />
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No Polish factory data available.</p>
      ) : (
      <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left p-2 font-medium whitespace-nowrap">Posting Date</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Document Number</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Creation Date</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Date of Update</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">BP Reference No.</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Warehouse Code</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Bill To</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Ship To</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Postcode</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Row Delivery Date</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Distribution Rule</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">CustomerCode</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Customer/Supplier Name</th>
              <th
                className="text-left p-2 font-medium whitespace-nowrap cursor-pointer hover:bg-muted/70 select-none"
                onClick={togglePartNumberSort}
              >
                <div className="flex items-center gap-1">Item No. {sortIcon}</div>
              </th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Item/Service Description</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Remaining Open Quantity</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Price after Discount</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Row Total</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">Document Total</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Warehouse Code2</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Pick List ID Number</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} className="border-b hover:bg-muted/30">
                <td className="p-2 whitespace-nowrap">{item.posting_date ?? ''}</td>
                <td className="p-2 whitespace-nowrap">{item.sales_order}</td>
                <td className="p-2 whitespace-nowrap">{item.creation_date ?? ''}</td>
                <td className="p-2 whitespace-nowrap">{item.date_of_update ?? ''}</td>
                <td className="p-2 whitespace-nowrap">{item.drop_number}</td>
                <td className="p-2 whitespace-nowrap">{item.warehouse_code}</td>
                <td className="p-2">{item.bill_to}</td>
                <td className="p-2">{item.delivery_address}</td>
                <td className="p-2 whitespace-nowrap">{item.delivery_postcode}</td>
                <td className="p-2 whitespace-nowrap">{item.row_delivery_date ?? ''}</td>
                <td className="p-2">{item.factory}</td>
                <td className="p-2 whitespace-nowrap">{item.customer_code}</td>
                <td className="p-2">{item.customer_name}</td>
                <td className="p-2 font-mono text-xs whitespace-nowrap">{item.part_number}</td>
                <td className="p-2">{item.description}</td>
                <td className="p-2 text-right">{item.open_qty}</td>
                <td className="p-2 text-right">{item.price_after_discount ?? ''}</td>
                <td className="p-2 text-right">{item.row_total ?? ''}</td>
                <td className="p-2 text-right">{item.document_total ?? ''}</td>
                <td className="p-2 whitespace-nowrap">{item.warehouse_code2}</td>
                <td className="p-2 whitespace-nowrap">{item.pick_list_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
